import React, { FC, useEffect, useState, useCallback } from 'react';
import { Badge, IconButton } from '@mui/material';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import { useHistory } from 'react-router-dom';

import messagingApi from '../../api/messaging/api';
import { useSocket } from '../../api/socket/SocketProvider';

const POLL_INTERVAL = 60000; // 60s fallback poll (in case socket misses something)

const NotificationBell: FC = () => {
    const [unreadCount, setUnreadCount] = useState(0);
    const history = useHistory();
    const { socket } = useSocket();

    const fetchUnread = useCallback(async () => {
        try {
            const data = await messagingApi.getUnreadCount();
            setUnreadCount(data?.count || 0);
        } catch {
            // silent fail
        }
    }, []);

    // Initial fetch + fallback poll
    useEffect(() => {
        fetchUnread();
        const interval = setInterval(fetchUnread, POLL_INTERVAL);
        return () => clearInterval(interval);
    }, [fetchUnread]);

    // Real-time: bump count on new message notification
    useEffect(() => {
        if (!socket) return;

        const handleNotify = () => {
            // Re-fetch exact count from server (more reliable than local increment)
            fetchUnread();
        };

        socket.on('message:notify', handleNotify);
        socket.on('message:read', handleNotify); // re-fetch when user reads a conversation
        return () => {
            socket.off('message:notify', handleNotify);
            socket.off('message:read', handleNotify);
        };
    }, [socket, fetchUnread]);

    // Re-fetch on tab focus and route changes (catches cases where socket events are lost)
    useEffect(() => {
        const handleVisibility = () => {
            if (document.visibilityState === 'visible') fetchUnread();
        };
        document.addEventListener('visibilitychange', handleVisibility);
        return () => document.removeEventListener('visibilitychange', handleVisibility);
    }, [fetchUnread]);

    useEffect(() => {
        return history.listen(() => fetchUnread());
    }, [history, fetchUnread]);

    const hasUnread = unreadCount > 0;

    return (
        <IconButton
            size="small"
            onClick={() => history.push('/messaging')}
            title={hasUnread ? `${unreadCount} unread message${unreadCount > 1 ? 's' : ''}` : 'Messages'}
            sx={{ color: 'inherit' }}
        >
            <Badge
                badgeContent={unreadCount}
                color="error"
                max={99}
                sx={{ '& .MuiBadge-badge': { fontSize: 10, height: 16, minWidth: 16 } }}
            >
                <ForumOutlinedIcon
                    fontSize="small"
                    sx={{ color: hasUnread ? '#FFB300' : 'inherit' }}
                />
            </Badge>
        </IconButton>
    );
};

export default NotificationBell;
